import React, { createContext, useContext, useEffect, useState } from 'react';

import { handoffState } from '@modules/support/utils/handoffState';

import ForethoughtConstants from '../constants';

const initialContext = {
  isLoading: false,
  isError: false,
  isOpen: false,
  open: () => {},
  close: () => {},
};

const Context = createContext<IForethoughtContext>(initialContext);
const { FORETHOUGHT_EVENTS } = ForethoughtConstants;
const { getHandoffState, setHandoffActive } = handoffState;

const ForethoughtContext: CustomFC = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const open = () => {
    if (window?.Forethought) {
      window.Forethought('widget', 'open');
    }
  };

  const close = () => {
    if (window?.Forethought) {
      window.Forethought('widget', 'close');
    }
  };

  useEffect(() => {
    const handleForethoughtEvents = (event: MessageEvent) => {
      if (event?.data) {
        const forethoughtEvent = event.data?.event;
        switch (forethoughtEvent) {
          case FORETHOUGHT_EVENTS.closed:
            setIsOpen(false);
            break;
          case FORETHOUGHT_EVENTS.error:
            setIsError(true);
            setIsLoading(false);
            window?.zE?.('messenger', 'show');
            break;
          case FORETHOUGHT_EVENTS.loaded:
            close();
            setIsLoading(false);
            break;
          case FORETHOUGHT_EVENTS.initializing:
            const currentHandoffState = getHandoffState();
            if (currentHandoffState) {
              window?.Forethought?.('widget', 'hide');
            }
            break;
          case FORETHOUGHT_EVENTS.handoffStarted:
            setIsLoading(true);
            setHandoffActive();
            break;
          case FORETHOUGHT_EVENTS.handoffCompleted:
            setIsLoading(false);
            break;
          case FORETHOUGHT_EVENTS.opened:
            setIsOpen(true);
            break;
        }
      }
    };

    const handleForethoughtStateChange = (event: Event) => {
      if (event instanceof CustomEvent) {
        if (event.detail?.isLoading !== undefined) setIsLoading(event.detail.isLoading);
        if (event.detail?.isError !== undefined) setIsError(event.detail.isError);
      }
    };

    window.addEventListener('message', handleForethoughtEvents);
    window.addEventListener(FORETHOUGHT_EVENTS.stateChanged, handleForethoughtStateChange);
    return () => {
      window.removeEventListener('message', handleForethoughtEvents);
      window.removeEventListener(FORETHOUGHT_EVENTS.stateChanged, handleForethoughtStateChange);
    };
  }, []);

  return (
    <Context.Provider value={{ open, close, isLoading, isError, isOpen }}>
      {children}
    </Context.Provider>
  );
};

export const useForethoughtContext = () => useContext(Context);
export default ForethoughtContext;
